import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/auth";
import { Upload, Link as LinkIcon, Trash2, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/lib/confirm";

const isVideoUrl = (u) => typeof u === "string" && /\.(mp4|webm|mov|quicktime)(\?|$)/i.test(u);
const isEmbed = (u) => typeof u === "string" && /(youtube\.com|youtu\.be|vimeo\.com)/i.test(u);

export default function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);
  const fileRef = useRef(null);
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/gallery");
      setItems(r.data || []);
    } catch {
      toast.error("Failed to load gallery");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addItem = async (payload) => {
    const nextOrder = (items.reduce((m, it) => Math.max(m, it.order || 0), 0) || 0) + 1;
    await api.post("/admin/gallery", { ...payload, order: nextOrder });
    toast.success("Added to gallery");
    load();
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const { url: fileUrl, media_type } = r.data;
      const publicUrl = fileUrl;
      if (media_type === "video") {
        await addItem({ image: "", video_url: publicUrl });
      } else {
        await addItem({ image: publicUrl, video_url: "" });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onAddUrl = async () => {
    const u = url.trim();
    if (!u) { toast.error("Paste a photo or video URL"); return; }
    setSavingUrl(true);
    try {
      if (isVideoUrl(u) || isEmbed(u)) {
        await addItem({ image: "", video_url: u });
      } else {
        await addItem({ image: u, video_url: "" });
      }
      setUrl("");
    } catch (err) {
      toast.error("Failed to add");
    } finally { setSavingUrl(false); }
  };

  const remove = async (id) => {
    const ok = await confirm({ title: "Delete this item?", description: "It'll be removed from the public gallery.", danger: true, confirmLabel: "Delete" });
    if (!ok) return;
    try { await api.delete(`/admin/gallery/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]">
      <div className="mb-8">
        <h1 className="font-display text-4xl uppercase leading-none">Gallery</h1>
        <p className="text-zinc-500 text-sm mt-2">Add photos or videos. They appear on the public gallery in the order you added them.</p>
      </div>

      {/* Add row */}
      <div className="grid md:grid-cols-2 gap-4 mb-10">
        <div className="card-surface p-6">
          <div className="eyebrow mb-3 flex items-center gap-2"><Upload size={12}/> Upload file</div>
          <p className="text-zinc-500 text-xs mb-4">JPG · PNG · GIF · WEBP · MP4 · WEBM · MOV — up to 8MB images / 50MB videos.</p>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,video/*"
            onChange={onUpload}
            disabled={uploading}
            className="hidden"
            data-testid="gallery-upload-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="btn-red inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="gallery-upload-btn"
          >
            {uploading ? <><Loader2 size={14} className="animate-spin"/> Uploading…</> : <><Upload size={14}/> Choose file</>}
          </button>
        </div>

        <div className="card-surface p-6">
          <div className="eyebrow mb-3 flex items-center gap-2"><LinkIcon size={12}/> Paste URL</div>
          <p className="text-zinc-500 text-xs mb-4">Any direct image URL, video URL, or YouTube / Vimeo link.</p>
          <div className="flex gap-2">
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://…"
              className="flex-1 bg-black border border-white/10 px-3 py-2 focus:border-red-600 outline-none"
              onKeyDown={e => e.key === "Enter" && onAddUrl()}
              data-testid="gallery-url-input"
            />
            <button
              onClick={onAddUrl}
              disabled={savingUrl}
              className="btn-red inline-flex items-center gap-2 disabled:opacity-60 whitespace-nowrap"
              data-testid="gallery-url-add-btn"
            >
              {savingUrl ? "Adding…" : "Add"}
            </button>
          </div>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-zinc-500 text-center py-16" data-testid="gallery-admin-empty">
          Nothing here yet — upload a file or paste a URL above.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-admin-grid">
          {items.map((g, i) => {
            const src = g.video_url || g.image;
            const isVid = !!g.video_url || isVideoUrl(g.image);
            const embed = isEmbed(g.video_url || "");
            return (
              <div key={g.id} className="relative group aspect-square overflow-hidden card-surface" data-testid={`gallery-admin-item-${i}`}>
                {embed ? (
                  <div className="w-full h-full grid place-items-center bg-black">
                    <div className="text-zinc-400 text-xs uppercase tracking-widest flex flex-col items-center gap-2">
                      <Play size={28} className="text-red-500"/> Video link
                    </div>
                  </div>
                ) : isVid ? (
                  <video src={src} className="w-full h-full object-cover" muted playsInline preload="metadata"/>
                ) : (
                  <img src={/\/api\/files\/[A-Za-z0-9-]+$/.test(g.image) ? `${g.image}?size=thumb` : g.image} loading="lazy" alt="" className="w-full h-full object-cover"/>
                )}
                <button
                  onClick={() => remove(g.id)}
                  className="absolute top-2 right-2 bg-black/80 hover:bg-red-600 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Delete"
                  data-testid={`gallery-admin-delete-${i}`}
                >
                  <Trash2 size={14}/>
                </button>
                {isVid && (
                  <div className="absolute bottom-2 left-2 bg-black/80 text-white text-[9px] uppercase tracking-widest px-2 py-1">Video</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
